/**
 * PostSwipeViewer — Instagram-style fullscreen viewer with TWO independent
 * swipe levels.
 *
 * Level 1 (default):  swipe LEFT/RIGHT cycles MEDIA inside the current post.
 *                     The post does NOT change. We expose page indicators
 *                     (1/3, 2/3 …) so the user can see where they are.
 *
 * Level 2 (boundary): when the user is on the LAST media and swipes left
 *                     again, OR on the FIRST media and swipes right, that
 *                     overflow swipe transitions to the next/previous post.
 *                     This matches Instagram's nested-swipe pattern.
 *
 * Explicit post navigation: ⏪/⏩ buttons (sm screens) and ↑/↓ keys jump
 * between posts directly without going through every media item.
 *
 * Video cleanup: navigating between either media OR posts calls
 * `stopAllAutoPlayVideos()` BEFORE updating the index, so the previous
 * video element is paused and muted within the same tick — no overlap.
 *
 * Sound: video auto-plays UNMUTED (the user has already tapped to open the
 * viewer, satisfying the user-gesture requirement). If the browser still
 * blocks unmuted autoplay, the AutoPlayVideo fallback kicks in muted +
 * shows a tap-to-unmute overlay.
 */
import { useEffect, useMemo, useRef, useState, useCallback, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Heart, MessageCircle, Send, Loader2, ChevronUp, ChevronDown, Music, Share2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AutoPlayVideo, { stopAllAutoPlayVideos } from './AutoPlayVideo';
import { stopAllFeedAudio } from '../hooks/useFeedAudio';
import { AudioPicker } from './AudioPicker';
import { getImageUrl } from '../utils/imageUrl';
import { useLanguage } from '../contexts/LanguageContext';
import { FollowButton } from './feed/SocialButtons';

// Tim's brief Feb 7, 2026 — KRITISK FIX: PostSwipeViewer ("Opslag 1/1"-modal)
// afspilte den 29s rendrede MP4 i stedet for den LIVE WebGL replay når
// brugeren klikkede et ride_summary-opslag fra forsiden eller feed-listen.
// Vi loader nu RideReplayPlayer (cinematic GPS+kapitler+drone) for posts
// af typen ride_summary med en ride_id. Highlight/MP4 vises ALDRIG her.
const RideReplayPlayer = lazy(() => import('./replay/RideReplayPlayer'));

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SWIPE_THRESHOLD = 60;
const SWIPE_DEAD_ZONE = 8;

// Build a unified media list from a post.
//
// Preferred source (when present): `post.media_ordered` — an array of
// `{url, type, timestamp}` we set when the post is created from a ride.
// Falls back to the legacy `videos[]` + `images[]` fields where we
// interleave by ARRAY INDEX (so the original capture sequence is preserved
// when an image and video were taken alternately) instead of grouping by
// type. Type-grouping was the bug we just fixed.
const collectMedia = (post) => {
  if (!post) return [];

  // 1. New, timestamped format — sort ascending and we're done
  const ordered = post.media_ordered || post.media;
  if (Array.isArray(ordered) && ordered.length > 0) {
    return [...ordered]
      .filter(m => m && (m.url || m.media_url))
      .sort((a, b) => {
        const ta = a.timestamp || a.captured_at || 0;
        const tb = b.timestamp || b.captured_at || 0;
        if (typeof ta === 'string' && typeof tb === 'string') return ta.localeCompare(tb);
        return Number(ta) - Number(tb);
      })
      .map((m, i) => ({
        kind: (m.type === 'video' || (m.url || m.media_url || '').match(/\.(mp4|mov|webm|m4v|3gp)/i)) ? 'video' : 'image',
        url: m.url || m.media_url,
        key: `m-${i}-${m.url || m.media_url}`,
      }));
  }

  // 2. Legacy fallback — interleave preserves whatever order the post
  //    creator stored. We zip images[i] with videos[i] alternately so
  //    a "photo, video, photo" sequence renders in that order rather
  //    than "videos first then photos".
  const videos = (post.videos || []).map((u) => ({ kind: 'video', url: u, key: `v-${u}` }));
  const images = (post.images || []).map((u) => ({ kind: 'image', url: u, key: `i-${u}` }));
  const out = [];
  const max = Math.max(videos.length, images.length);
  for (let i = 0; i < max; i++) {
    if (images[i]) out.push(images[i]);
    if (videos[i]) out.push(videos[i]);
  }
  return out;
};

export default function PostSwipeViewer({
  posts,
  startIndex = 0,
  onClose,
  onLikeChanged,
  onUpdated,
  currentUserId,
  // ── User-feed mode props ─────────────────────────────────────────
  // When the caller has filtered `posts` down to a single user's
  // opslag, pass `userFilterMode={true}` + the user's metadata so
  // the viewer can render the "Du ser kun opslag fra X" header. The
  // `onClearUserFilter` callback brings the user back to the full
  // mixed feed (the parent restores the unfiltered list).
  userFilterMode = false,
  filteredUser = null,
  onClearUserFilter,
}) {
  const { t } = useLanguage();
  const [postIdx, setPostIdx] = useState(startIndex);
  const [mediaIdx, setMediaIdx] = useState(0);
  const [musicPickerOpen, setMusicPickerOpen] = useState(false);
  const postIdxRef = useRef(startIndex);
  const mediaIdxRef = useRef(0);
  useEffect(() => { postIdxRef.current = postIdx; }, [postIdx]);
  useEffect(() => { mediaIdxRef.current = mediaIdx; }, [mediaIdx]);

  const post = posts[postIdx];
  const media = useMemo(() => collectMedia(post), [post]);
  const total = posts.length;
  const mediaCount = media.length;
  const current = media[mediaIdx] || null;
  const isOwner = !!currentUserId && currentUserId === post?.user_id;

  // Native share with graceful fallback to copy-to-clipboard so the user
  // can share the post URL out of the viewer without opening a new screen.
  const sharePost = useCallback(async () => {
    if (!post) return;
    const url = `${window.location.origin}/feed?post=${post.id}`;
    const title = post.title || 'BareMC opslag';
    const text = post.text ? post.text.slice(0, 140) : '';
    if (navigator.share) {
      try { await navigator.share({ title, text, url }); return; } catch { /* user cancelled */ return; }
    }
    try { await navigator.clipboard.writeText(url); toast.success(t('common.linkCopied')); }
    catch { toast.error(t('toasts.couldNotShare')); }
  }, [post, t]);

  // Persist new music choice on the post in-place. Uses PATCH so the post
  // owner can switch tracks without leaving the viewer.
  const applyTrack = useCallback(async (track) => {
    if (!post) return;
    setMusicPickerOpen(false);
    const payload = {};
    if (!track) {
      payload.audio_track_id = null;
      payload.audio_track = null;
    } else if (track.is_jamendo || track.source === 'jamendo') {
      payload.audio_track = {
        id: track.id,
        title: track.title,
        artist: track.artist,
        url: track.url,
        license_url: track.license_url || null,
      };
      payload.audio_track_id = null;
    } else {
      payload.audio_track_id = track.id;
      payload.audio_track = null;
    }
    try {
      const res = await axios.patch(`${API_URL}/posts/${post.id}`, payload);
      const updated = res.data?.post || res.data || {};
      // Echo a minimal patch onto the local post so UI updates immediately.
      const patch = {
        audio_track_id: updated.audio_track_id ?? payload.audio_track_id ?? null,
        audio_track: updated.audio_track ?? (payload.audio_track || null),
      };
      Object.assign(post, patch);
      onUpdated?.(post.id, patch);
      toast.success(track ? t('feed.swipeViewer.musicUpdated').replace('{title}', track.title) : t('feed.swipeViewer.musicRemoved'));
    } catch (err) {
      toast.error(t('feed.swipeViewer.couldNotUpdateMusic') + ': ' + (err?.response?.data?.detail || err?.message || ''));
    }
  }, [post, onUpdated, t]);

  // Per-post comment cache
  const commentsCache = useRef({});
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting] = useState(false);

  // Touch tracking
  const touchStart = useRef({ x: 0, y: 0, t: 0, active: false, locked: null });
  const [dragX, setDragX] = useState(0);
  const dragRef = useRef(0);

  // Comments load on post change
  const loadComments = useCallback(async (p) => {
    if (!p) return;
    if (commentsCache.current[p.id]) { setComments(commentsCache.current[p.id]); return; }
    setCommentsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/posts/${p.id}/comments`);
      const list = res.data?.comments || res.data || [];
      commentsCache.current[p.id] = list;
      setComments(list);
    } catch { setComments([]); }
    finally { setCommentsLoading(false); }
  }, []);
  useEffect(() => { loadComments(post); }, [post?.id, loadComments]);

  // Reset media index when switching post
  useEffect(() => { setMediaIdx(0); }, [postIdx]);

  // ---- Navigation primitives ----
  const stopAllVideos = () => {
    try { stopAllAutoPlayVideos(); } catch { /* ignore */ }
    try { stopAllFeedAudio(); } catch { /* ignore */ }
  };
  const goPostDelta = useCallback((delta) => {
    const next = postIdxRef.current + delta;
    if (next < 0 || next >= posts.length) return false;
    stopAllVideos();
    setPostIdx(next);
    setMediaIdx(0);
    dragRef.current = 0; setDragX(0);
    return true;
  }, [posts.length]);

  // Swipe handler — media-first, post-on-overflow.
  // Returns true if the swipe was consumed.
  const swipe = useCallback((direction) => {
    // direction: -1 = left (next media/post), +1 = right (prev media/post)
    const m = mediaIdxRef.current;
    const total = collectMedia(posts[postIdxRef.current]).length;
    if (direction < 0) {
      // Going forward
      if (m + 1 < total) {
        stopAllVideos();
        setMediaIdx(m + 1);
        dragRef.current = 0; setDragX(0);
        return true;
      }
      // Overflow → next post
      return goPostDelta(+1);
    }
    // direction > 0, going backward
    if (m - 1 >= 0) {
      stopAllVideos();
      setMediaIdx(m - 1);
      dragRef.current = 0; setDragX(0);
      return true;
    }
    return goPostDelta(-1);
  }, [posts, goPostDelta]);

  // ---- Touch handlers — own only horizontal drags after dead zone ----
  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY, t: Date.now(), active: true, locked: null };
    dragRef.current = 0;
  };
  const onTouchMove = (e) => {
    if (!touchStart.current.active) return;
    const t = e.touches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    if (touchStart.current.locked === null) {
      if (Math.abs(dx) < SWIPE_DEAD_ZONE && Math.abs(dy) < SWIPE_DEAD_ZONE) return;
      touchStart.current.locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }
    if (touchStart.current.locked !== 'h') return;
    if (e.cancelable) e.preventDefault();
    dragRef.current = dx;
    setDragX(dx);
  };
  const onTouchEnd = () => {
    if (!touchStart.current.active) return;
    const dx = dragRef.current;
    const owned = touchStart.current.locked === 'h';
    touchStart.current.active = false;
    touchStart.current.locked = null;
    if (!owned) { setDragX(0); return; }
    if (dx <= -SWIPE_THRESHOLD) swipe(-1);
    else if (dx >= SWIPE_THRESHOLD) swipe(+1);
    else { dragRef.current = 0; setDragX(0); }
  };

  // ---- Keyboard ----
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') swipe(+1);
      else if (e.key === 'ArrowRight') swipe(-1);
      else if (e.key === 'ArrowDown') goPostDelta(+1);
      else if (e.key === 'ArrowUp') goPostDelta(-1);
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [swipe, goPostDelta, onClose]);

  // ---- Aggressive preload: next media in current post + neighbour-post first media ----
  const preloadList = useMemo(() => {
    const out = [];
    if (media[mediaIdx + 1]) out.push(media[mediaIdx + 1]);
    [posts[postIdx - 1], posts[postIdx + 1]].forEach((p) => {
      const m = collectMedia(p)[0];
      if (m) out.push(m);
    });
    return out;
  }, [media, mediaIdx, posts, postIdx]);

  if (!post) return null;

  // ---- Like + comment ----
  const toggleLike = async () => {
    try {
      await axios.post(`${API_URL}/posts/${post.id}/react`, { reaction: 'love' });
      const newLiked = !post.is_liked;
      post.is_liked = newLiked;
      post.like_count = (post.like_count || 0) + (newLiked ? 1 : -1);
      onLikeChanged?.(post.id, newLiked, post.like_count);
      setPostIdx((i) => i);
    } catch { toast.error(t('feed.swipeViewer.couldNotLike')); }
  };

  const submitComment = async () => {
    const txt = commentText.trim();
    if (!txt || posting) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API_URL}/posts/${post.id}/comments`, { text: txt });
      const newComment = res.data?.comment || res.data;
      const next = [newComment, ...(commentsCache.current[post.id] || [])];
      commentsCache.current[post.id] = next;
      setComments(next);
      setCommentText('');
      post.comment_count = (post.comment_count || 0) + 1;
    } catch { toast.error(t('feed.swipeViewer.commentFailed')); }
    finally { setPosting(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[2400] bg-black flex flex-col" data-testid="post-swipe-viewer">
      {/* Hidden preload */}
      <div aria-hidden="true" style={{ position: 'fixed', width: 1, height: 1, opacity: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {preloadList.map((m) => (m.kind === 'video' ? (
          <video key={m.key} src={getImageUrl(m.url)} preload="auto" muted playsInline />
        ) : (
          <img key={m.key} src={getImageUrl(m.url)} alt="" />
        )))}
      </div>

      {/* Header */}
      <header className="flex items-center justify-between p-3 border-b border-white/10 flex-shrink-0" style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" data-testid="swipe-close" aria-label={t('common.close')}>
          <X className="w-5 h-5 text-white" />
        </button>
        <div className="flex flex-col items-center">
          <span className="text-white text-[11px] font-bold tabular-nums" data-testid="swipe-counter">{t('feed.swipeViewer.postCounter').replace('{i}', postIdx + 1).replace('{n}', total)}</span>
          {mediaCount > 1 && (
            <span className="text-white/50 text-[10px] tabular-nums" data-testid="swipe-media-counter">{t('feed.swipeViewer.mediaCounter').replace('{i}', mediaIdx + 1).replace('{n}', mediaCount)}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Explicit post-jump buttons — always available, regardless of media position */}
          <button
            onClick={() => goPostDelta(-1)}
            disabled={postIdx === 0}
            className="p-1.5 rounded-lg hover:bg-white/10 disabled:opacity-30"
            data-testid="swipe-prev-post"
            aria-label={t('feed.swipeViewer.previousPost')}
          >
            <ChevronUp className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={() => goPostDelta(+1)}
            disabled={postIdx === total - 1}
            className="p-1.5 rounded-lg hover:bg-white/10 disabled:opacity-30"
            data-testid="swipe-next-post"
            aria-label={t('feed.swipeViewer.nextPost')}
          >
            <ChevronDown className="w-4 h-4 text-white" />
          </button>
        </div>
      </header>

      {/* User-filter banner — only visible when the parent is showing
          a single user's opslag. Tells the viewer in plain Danish
          that this is a filtered view and offers one tap back to the
          full feed. Sticky, glassy, never blocks media. */}
      {userFilterMode && filteredUser && (
        <div
          className="flex-shrink-0 flex items-center gap-3 px-4 py-2.5 border-b border-white/10"
          style={{ background: 'linear-gradient(180deg, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0.05) 100%)' }}
          data-testid="user-filter-banner"
        >
          {filteredUser.avatar_url ? (
            <img
              src={getImageUrl(filteredUser.avatar_url)}
              alt={filteredUser.name || 'avatar'}
              className="w-9 h-9 rounded-full object-cover border-2 border-emerald-400/60 flex-shrink-0"
              loading="lazy"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-emerald-500/30 border-2 border-emerald-400/60 flex items-center justify-center text-emerald-100 text-sm font-bold flex-shrink-0">
              {(filteredUser.name || '?')[0].toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300/80">
              Du ser kun opslag fra
            </div>
            <div className="text-sm font-bold text-white truncate">
              {filteredUser.name || 'Ukendt bruger'}
            </div>
          </div>
          {/* Follow-knappen sidder lige her — natural moment for a follow
              when the viewer is curious enough to keep swiping through
              someone's opslag. We only show it when (a) the viewer is
              NOT the same user as the filtered one, and (b) we managed
              to load their current follow-state. */}
          {currentUserId && filteredUser.id && currentUserId !== filteredUser.id && (
            <BannerFollowButton
              userId={filteredUser.id}
              key={`bf-${filteredUser.id}`}
            />
          )}
          <button
            type="button"
            onClick={onClearUserFilter}
            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white text-[11px] font-semibold flex items-center gap-1.5 flex-shrink-0"
            data-testid="user-filter-back-to-feed"
            aria-label="Tilbage til hele feedet"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Hele feedet
          </button>
        </div>
      )}

      {/* Touch surface */}
      <div
        className="relative flex-1 flex flex-col overflow-hidden"
        style={{ touchAction: 'pan-y' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div
          className="flex flex-col h-full will-change-transform"
          style={{
            transform: `translateX(${dragX}px)`,
            transition: dragX === 0 ? 'transform 180ms ease-out' : 'none',
          }}
        >
          {/* Media — single item driven by mediaIdx; key forces unmount of
              previous video element when index changes.
              Tim's brief Feb 26: backdrop-poster ALTID rendret bag videoen
              så vi aldrig viser sort flash mens MP4'en buffrer.

              Tim's brief Feb 7, 2026 — TRE-PRODUKT REGEL: når posten er en
              `ride_summary` med en ride_id, render LIVE WebGL RideReplayPlayer
              (cinematic GPS+kapitler+drone) — IKKE den 29s rendrede MP4.
              Det er den oplevelse brugeren ser i editor-forhåndsvisningen. */}
          {/* Tim's brief Feb 8, 2026 — UDVIDET DETECTION:
              Tidligere krævede vi `post_type === 'ride_summary'`. Det
              betød at posts med post_type='reel' (eller andre varianter)
              med en gyldig ride_id faldt igennem og afspilte den gamle
              MP4 fra videos[0]. Vi tjekker NU på selve tilstedeværelsen
              af en ride_id — uanset post_type. */}
          {(post.ride_summary?.ride_id || post.ride_id) ? (
            <div
              className="bg-black relative w-full"
              style={{ aspectRatio: '4 / 5', minHeight: 320 }}
              data-testid={`swipe-live-replay-${post.id}`}
            >
              <Suspense fallback={
                <div className="absolute inset-0 flex items-center justify-center text-white/60">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              }>
                <RideReplayPlayer
                  rideId={post.ride_summary?.ride_id || post.ride_id}                  mediaId={`swipe-${post.id}`}
                  rider={{
                    name: post.user_name,
                    club: post.user_club_name,
                    clubArea: post.user_club_area,
                    bikeType: post.ride_summary?.intro_bike_type
                      || post.ride_summary?.rider_bike_type
                      || 'chopper',
                    bikeColor: post.ride_summary?.rider_bike_color,
                    backPatchUrl: post.user_back_patch_url,
                    bikeImageUrl: null,
                  }}
                  summary={{
                    distanceKm: post.ride_summary?.distance_km != null
                      ? Number(post.ride_summary.distance_km).toFixed(1).replace('.', ',')
                      : null,
                    durationSec: post.ride_summary?.duration_sec != null
                      ? Number(post.ride_summary.duration_sec) : null,
                    durationLabel: post.ride_summary?.duration_sec != null
                      ? `${Math.round(post.ride_summary.duration_sec / 60)} min`
                      : null,
                    routeName: post.ride_summary?.route_name || post.text || null,
                  }}
                  posterUrl={post.poster_url || (post.images && post.images[0]
                    ? getImageUrl(post.images[0]) : null)}
                  fillContainer
                  mapView="street"
                  testId={`swipe-live-replay-player-${post.id}`}
                />
              </Suspense>
            </div>
          ) : (
          <div
            className="bg-black flex items-center justify-center relative"
            style={{
              minHeight: 240,
              maxHeight: '60vh',
              backgroundImage: post.poster_url
                ? `url(${getImageUrl(post.poster_url)})`
                : (post.images?.[0] ? `url(${getImageUrl(post.images[0])})` : 'none'),
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: '#0a0f0c',
            }}
          >
            {/* Soft darkening on top of the backdrop so any overlaid UI
                (controls, dots) stays legible — fades when the video paints
                over. */}
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: 'rgba(0,0,0,0.35)' }}
              aria-hidden="true"
            />
            {current?.kind === 'video' ? (
              <AutoPlayVideo
                key={current.key + '-' + post.id}
                src={current.url}
                poster={post.poster_url || post.images?.[0] || null}
                showControls
                showMute={false}
                className="max-w-full max-h-[60vh] object-contain bg-transparent relative z-10"
                testId={`swipe-video-${post.id}-${mediaIdx}`}
                startUnmuted
                // Auto-loop: when the MP4 ends, restart from frame 0
                // so visual + audio reset together. Per Tim's brief
                // 14. Feb: "Den skal bare starte automatisk forfra".
                // Play/pause control is exposed via the native HTML5
                // controls bar (showControls=true above).
                loop={true}
              />
            ) : current?.kind === 'image' ? (
              <img src={getImageUrl(current.url)} alt="" className="max-w-full max-h-[60vh] object-contain relative z-10" loading="eager" />
            ) : (
              <div className="text-white/40 text-sm py-12 relative z-10">Ingen medier</div>
            )}

            {/* Page dots — only shown if post has multiple media */}
            {mediaCount > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid="swipe-media-dots">
                {media.map((_, i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${i === mediaIdx ? 'bg-white w-4' : 'bg-white/40'}`}
                  />
                ))}
              </div>
            )}
          </div>
          )}

          {/* Caption + actions */}
          <div className="px-4 py-3 border-b border-white/10 flex-shrink-0 bg-zinc-950">
            <div className="flex items-center gap-2 mb-2">
              {post.user_profile_image && (
                <img src={getImageUrl(post.user_profile_image)} alt="" className="w-7 h-7 rounded-full object-cover" />
              )}
              <span className="text-white text-sm font-bold">{post.user_name || 'Bruger'}</span>
            </div>
            {(post.title || post.text) && (
              <p className="text-white/90 text-sm whitespace-pre-wrap" data-testid="swipe-caption">
                {post.title ? <span className="font-bold">{post.title} </span> : null}
                {post.text}
              </p>
            )}
            <div className="flex items-center gap-4 mt-3">
              <button onClick={toggleLike} className="flex items-center gap-1 text-white/80 hover:text-pink-400" data-testid="swipe-like">
                <Heart className={`w-5 h-5 ${post.is_liked ? 'text-pink-500 fill-pink-500' : ''}`} />
                <span className="text-xs">{post.like_count || 0}</span>
              </button>
              <div className="flex items-center gap-1 text-white/80">
                <MessageCircle className="w-5 h-5" />
                <span className="text-xs">{post.comment_count || 0}</span>
              </div>
              <button
                onClick={sharePost}
                className="flex items-center gap-1 text-white/80 hover:text-[#00FF88]"
                data-testid="swipe-share"
                aria-label={t('common.share')}
              >
                <Share2 className="w-5 h-5" />
              </button>
              {isOwner && (
                <button
                  onClick={() => setMusicPickerOpen(true)}
                  className="flex items-center gap-1 text-white/80 hover:text-[#00FF88] ml-auto"
                  data-testid="swipe-edit-music"
                  aria-label={t('feed.swipeViewer.music')}
                >
                  <Music className={`w-5 h-5 ${post.audio_track ? 'text-[#00FF88] fill-[#00FF88]' : ''}`} />
                  <span className="text-[10px] font-bold uppercase tracking-wide">{t('feed.swipeViewer.music')}</span>
                </button>
              )}
            </div>
            {post.audio_track?.title && (
              <div className="mt-2 flex items-center gap-1.5 text-white/60 text-[11px]" data-testid="swipe-track-label">
                <Music className="w-3 h-3 text-[#00FF88]" />
                <span className="truncate">{post.audio_track.title}{post.audio_track.artist ? ` · ${post.audio_track.artist}` : ''}</span>
              </div>
            )}
          </div>

          {/* Comments */}
          <div className="flex-1 overflow-y-auto bg-zinc-950" data-testid="swipe-comments" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}>
            {commentsLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-white/40 animate-spin" /></div>
            ) : comments.length === 0 ? (
              <p className="text-center text-white/40 text-sm py-6">{t('feed.swipeViewer.beFirstToComment')}</p>
            ) : (
              <ul className="px-4 py-3 space-y-3">
                {comments.map((c) => (
                  <li key={c.id} className="flex items-start gap-2" data-testid={`swipe-comment-${c.id}`}>
                    {c.user_profile_image ? (
                      <img src={getImageUrl(c.user_profile_image)} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-zinc-700 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs">
                        <span className="font-bold text-white mr-1">{c.user_name || 'Bruger'}</span>
                        <span className="text-white/80 break-words">{c.text}</span>
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Composer */}
          <div className="p-3 border-t border-white/10 flex items-center gap-2 bg-zinc-950 flex-shrink-0" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))', touchAction: 'manipulation' }}>
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) submitComment(); }}
              placeholder={t('feed.swipeViewer.commentPlaceholder')}
              className="flex-1 px-3 py-2 rounded-full bg-white/5 text-white text-sm placeholder-white/40 border border-white/10 focus:border-[#00FF88] outline-none"
              data-testid="swipe-comment-input"
            />
            <button
              onClick={submitComment}
              disabled={!commentText.trim() || posting}
              className="w-9 h-9 rounded-full bg-[#00FF88] text-zinc-900 flex items-center justify-center disabled:opacity-50 active:scale-95"
              data-testid="swipe-comment-submit"
              aria-label={t('common.send')}
            >
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Desktop side arrows — operate at MEDIA level (with overflow to post) */}
        {(mediaIdx > 0 || postIdx > 0) && (
          <button
            onClick={() => swipe(+1)}
            className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur items-center justify-center text-white hover:bg-black/80 z-10"
            data-testid="swipe-prev-media"
            aria-label={t('common.previous')}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {(mediaIdx < mediaCount - 1 || postIdx < total - 1) && (
          <button
            onClick={() => swipe(-1)}
            className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur items-center justify-center text-white hover:bg-black/80 z-10"
            data-testid="swipe-next-media"
            aria-label={t('feed.swipeViewer.next')}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Inline music picker overlay — owner edits music WITHOUT leaving
          the viewer. Sits ABOVE the viewer (z-2500 > viewer's z-2400). */}
      <div className="relative" style={{ zIndex: 2500 }}>
        <AudioPicker
          open={musicPickerOpen}
          onClose={() => setMusicPickerOpen(false)}
          currentTrackId={post?.audio_track?.id}
          onPick={applyTrack}
        />
      </div>
    </div>,
    document.body
  );
}


/**
 * BannerFollowButton — tiny wrapper that fetches the viewer's current
 * `is_following` state for a target user on mount, then renders the
 * shared FollowButton with that initial state. Lives only in the
 * user-filter banner. We deliberately don't preload this for every
 * post in the feed — fetching once when the banner appears is
 * cheaper and the UX impact is invisible (button shows "Følg" by
 * default while the fetch resolves, ~150ms on typical mobile).
 */
function BannerFollowButton({ userId }) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/users/${userId}`);
        if (!cancelled) {
          setIsFollowing(!!res.data?.is_following);
          setReady(true);
        }
      } catch {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);
  if (!ready) {
    // Tiny placeholder so the layout doesn't reflow when the button
    // appears. Same size as the rendered button.
    return <span className="inline-block w-[68px] h-7 flex-shrink-0" aria-hidden="true" />;
  }
  return (
    <div className="flex-shrink-0">
      <FollowButton
        userId={userId}
        isFollowing={isFollowing}
        size="sm"
        testId={`user-filter-follow-${userId}`}
      />
    </div>
  );
}
